import { DeleteOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Checkbox, Spin, Table, Upload } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { FC, useMemo } from "react";
import {
  DS_ROW_KEY,
  OTHERS_ROW_KEY,
  isIpOrEmergencyVisit,
  isSyntheticReconRow,
} from "../../../utils/reconBillUploadUtils";
import {
  isAmountMismatch,
  isBillNoMismatch,
  isDateMismatch,
  isMismatch,
} from "../../../utils/reconBillMismatchUtils";

export type ReconBillUploadRow = {
  key: number;
  reconId: number;
  billNo?: string;
  billDate?: string;
  doa?: string;
  dod?: string;
  billAmount?: string;
  ipNumber?: string;
  documentKind?: string;
  documentId?: string;
  documentUrl?: string;
  documentLabelType?: string;
  documentName?: string;
  aiPatientName?: string;
  aiUhid?: string;
  aiDoa?: string;
  aiDod?: string;
  cghsTotal?: string;
  aiReadStatus?: string;
  aiBillNo?: string;
  billNoMatched?: boolean;
  /** True while pre-QC OCR billHeader is in progress for this row's document */
  ocrLoading?: boolean;
  cancelled?: boolean;
  cancelledReason?: string;
  cancelledByName?: string;
  cancelledDateTime?: string;
};

type Props = {
  rows: ReconBillUploadRow[];
  loading: boolean;
  selectedRowKeys: number[];
  onSelectionChange: (keys: number[]) => void;
  onUpload: (file: File, reconIds: number[]) => void;
  uploadingReconIds: number[];
  deletingReconIds: number[];
  cancellingReconIds?: number[];
  pendingCancelReconId?: number | null;
  uploadDisabled?: boolean;
  othersUploadDisabled?: boolean;
  onPreview: (record: ReconBillUploadRow) => void;
  onDelete: (record: ReconBillUploadRow) => void;
  onCancelBill?: (record: ReconBillUploadRow) => void;
  expectedPatientName?: string;
  expectedUhid?: string;
  visitType?: string;
};

const HEADER_BG = "#004c8f";
const HEADER_COLOR = "#ffffff";
const DATA_BLUE = "#004c8f";
const CANCELLED_TEXT = "#595959";
const MISMATCH_RED = "#cf1322";

const getDocumentDisplayName = (record: ReconBillUploadRow) => {
  if (record.documentName) {
    return record.documentName;
  }
  if (record.documentUrl) {
    const parts = record.documentUrl.split(/[\\/]/);
    const last = parts[parts.length - 1];
    if (last) {
      try {
        return decodeURIComponent(last.split("?")[0]);
      } catch {
        return last.split("?")[0];
      }
    }
  }
  return record.documentLabelType || "Document";
};

const headerCellStyle = () => ({
  style: {
    background: HEADER_BG,
    color: HEADER_COLOR,
    // fontWeight: 700,
    textTransform: "uppercase" as const,
  },
});

const blueText = (value: any, cancelled?: boolean) => (
  <span
    style={{
      color: cancelled ? CANCELLED_TEXT : DATA_BLUE,
      fontWeight: cancelled ? 400 : 600,
    }}
  >
    {value || "-"}
  </span>
);

/** Show date only (strip time) for DOA/DOD display. */
const formatDateOnly = (value?: string | null) => {
  if (!value) {
    return "-";
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return "-";
  }
  // ISO / SQL: 2025-07-08T10:00:00 or 2025-07-08 10:00:00
  const isoMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) {
    const [year, month, day] = isoMatch[1].split("-");
    return `${day}-${month}-${year}`;
  }
  // Already dd-MM-yyyy or dd/MM/yyyy with optional time
  const dmyMatch = trimmed.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{2,4})/);
  if (dmyMatch) {
    const day = dmyMatch[1].padStart(2, "0");
    const month = dmyMatch[2].padStart(2, "0");
    const year =
      dmyMatch[3].length === 2 ? `20${dmyMatch[3]}` : dmyMatch[3];
    return `${day}-${month}-${year}`;
  }
  // Fallback: take part before space/T
  return trimmed.split(/[T\s]/)[0] || trimmed;
};

const hasGrayValue = (value?: string | null) =>
  Boolean(value && String(value).trim() && String(value).trim() !== "-");

const shouldShowOcrSpinner = (
  record: ReconBillUploadRow,
  value?: string | null,
) => Boolean(record.ocrLoading) && !hasGrayValue(value);

const ReconBillUploadTable: FC<Props> = ({
  rows,
  loading,
  selectedRowKeys,
  onSelectionChange,
  onUpload,
  uploadingReconIds,
  deletingReconIds,
  cancellingReconIds = [],
  pendingCancelReconId = null,
  uploadDisabled = false,
  othersUploadDisabled = false,
  onPreview,
  onDelete,
  onCancelBill,
  expectedPatientName,
  expectedUhid,
  visitType,
}) => {
  const isIpEmergency = isIpOrEmergencyVisit(visitType);
  const selectedIds = selectedRowKeys.map(Number);
  const hasMultiSelection = selectedIds.length > 1;
  const selectedRowsWithoutDocument = selectedIds.filter((reconId) => {
    const row = rows.find((item) => item.reconId === reconId);
    return (
      row &&
      !row.documentId &&
      !row.cancelled &&
      !isSyntheticReconRow(row)
    );
  });
  const isBulkUploading = selectedRowsWithoutDocument.some((id) =>
    uploadingReconIds.includes(id),
  );
  const isSelected = (reconId: number) => selectedIds.includes(reconId);

  const documentOwnerReconId = useMemo(() => {
    const owners = new Map<string, number>();
    rows.forEach((row) => {
      if (!row.documentId || row.cancelled || isSyntheticReconRow(row)) {
        return;
      }
      const key = String(row.documentId);
      if (!owners.has(key)) {
        owners.set(key, row.reconId);
      }
    });
    return owners;
  }, [rows]);

  const isSharedDocumentOwner = (record: ReconBillUploadRow) => {
    if (!record.documentId || isSyntheticReconRow(record)) {
      return true;
    }
    return (
      documentOwnerReconId.get(String(record.documentId)) === record.reconId
    );
  };

  const isDsRow = (record: ReconBillUploadRow) =>
    record.documentKind === "DS" || record.reconId === DS_ROW_KEY;

  const isOthersRow = (record: ReconBillUploadRow) =>
    record.documentKind === "OTHERS" || record.reconId === OTHERS_ROW_KEY;

  const isRowUploadDisabled = (record: ReconBillUploadRow) =>
    isOthersRow(record) ? othersUploadDisabled : uploadDisabled;

  const getDocumentKindLabel = (record: ReconBillUploadRow) => {
    if (isOthersRow(record)) {
      return "OTHERS";
    }
    if (isDsRow(record)) {
      return "DS";
    }
    return "BILL";
  };

  const renderUploadCell = (record: ReconBillUploadRow) => {
    if (record.cancelled) {
      return <span style={{ color: CANCELLED_TEXT }}>Cancelled</span>;
    }

    const rowLocked = isRowUploadDisabled(record);
    const isRowUploading = uploadingReconIds.includes(record.reconId);
    const isRowDeleting = deletingReconIds.includes(record.reconId);
    const isRowCancelling =
      cancellingReconIds.includes(record.reconId) ||
      pendingCancelReconId === record.reconId;

    if (record.documentId) {
      const fileName = getDocumentDisplayName(record);
      const isOwner = isSharedDocumentOwner(record);

      if (!isOwner) {
        return (
          <span style={{ color: DATA_BLUE, fontSize: 12 }} title={fileName}>
            {fileName.length > 18 ? `${fileName.slice(0, 18)}...` : fileName}
          </span>
        );
      }

      return (
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <span
            style={{ color: DATA_BLUE, fontSize: 12, maxWidth: 110 }}
            title={fileName}
          >
            {fileName.length > 14 ? `${fileName.slice(0, 14)}...` : fileName}
          </span>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => onPreview(record)}
            disabled={isRowDeleting || isRowCancelling}
            style={{ color: DATA_BLUE, padding: 0 }}
          />
          {!rowLocked && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={isRowDeleting}
              onClick={() => onDelete(record)}
              disabled={isRowUploading || isRowCancelling}
              style={{ padding: 0 }}
            />
          )}
        </div>
      );
    }
    if (rowLocked) {
      return <span style={{ color: "#999" }}>-</span>;
    }
    if (
      hasMultiSelection &&
      isSelected(record.reconId) &&
      !isSyntheticReconRow(record)
    ) {
      return <span style={{ color: "#999" }}>-</span>;
    }
    return (
      <Upload
        accept=".pdf"
        showUploadList={false}
        disabled={
          rowLocked || isRowDeleting || isBulkUploading || isRowCancelling
        }
        beforeUpload={(file) => {
          onUpload(file, [record.reconId]);
          return false;
        }}
      >
        <Button
          type="link"
          icon={<UploadOutlined />}
          loading={isRowUploading}
          disabled={
            rowLocked ||
            isRowDeleting ||
            isBulkUploading ||
            isRowCancelling ||
            (uploadingReconIds.length > 0 && !isRowUploading)
          }
          style={{ color: DATA_BLUE }}
        />
      </Upload>
    );
  };

  const patientNameColumn: ColumnsType<ReconBillUploadRow>[number] = {
    title: "PATIENT NAME",
    dataIndex: "aiPatientName",
    key: "aiPatientName",
    onHeaderCell: headerCellStyle,
    onCell: (record) => ({
      style: { background: record.cancelled ? "#f0f0f0" : "#f5f5f5" },
    }),
    render: (value, record) => {
      if (shouldShowOcrSpinner(record, value)) {
        return <Spin size="small" />;
      }
      if (record.cancelled) {
        return <span style={{ color: CANCELLED_TEXT }}>{value || "-"}</span>;
      }
      const uhidMatches = !isMismatch(record.aiUhid, expectedUhid);
      const showNameMismatch =
        !uhidMatches && isMismatch(value, expectedPatientName);
      return (
        <span
          style={{
            color: showNameMismatch ? MISMATCH_RED : DATA_BLUE,
            fontWeight: 600,
          }}
        >
          {value || "-"}
        </span>
      );
    },
  };

  const uhidColumn: ColumnsType<ReconBillUploadRow>[number] = {
    title: "UHID",
    dataIndex: "aiUhid",
    key: "aiUhid",
    onHeaderCell: headerCellStyle,
    onCell: (record) => {
      const mismatch =
        !record.cancelled &&
        !record.ocrLoading &&
        isMismatch(record.aiUhid, expectedUhid);
      return {
        style: {
          background: record.cancelled ? "#f0f0f0" : "#f5f5f5",
          ...(mismatch
            ? { outline: `1px solid ${MISMATCH_RED}`, outlineOffset: -1 }
            : {}),
        },
      };
    },
    render: (value, record) => {
      if (shouldShowOcrSpinner(record, value)) {
        return <Spin size="small" />;
      }
      const mismatch =
        !record.cancelled && isMismatch(value, expectedUhid);
      return (
        <span
          style={{
            color: record.cancelled
              ? CANCELLED_TEXT
              : mismatch
                ? MISMATCH_RED
                : DATA_BLUE,
            fontWeight: record.cancelled ? 400 : 600,
          }}
        >
          {value || "-"}
        </span>
      );
    },
  };

  const doaColumn: ColumnsType<ReconBillUploadRow>[number] = {
    title: "DOA",
    dataIndex: "aiDoa",
    key: "aiDoa",
    onHeaderCell: headerCellStyle,
    onCell: (record) => ({
      style: { background: record.cancelled ? "#f0f0f0" : "#f5f5f5" },
    }),
    render: (_value, record) => {
      if (shouldShowOcrSpinner(record, record.aiDoa)) {
        return <Spin size="small" />;
      }
      if (record.cancelled) {
        return (
          <span style={{ color: CANCELLED_TEXT }}>
            {formatDateOnly(record.aiDoa)}
          </span>
        );
      }
      const mismatch =
        !isSyntheticReconRow(record) &&
        isDateMismatch(record.aiDoa, record.doa);
      return (
        <span
          style={{
            color: mismatch ? MISMATCH_RED : DATA_BLUE,
            fontWeight: 600,
          }}
        >
          {formatDateOnly(record.aiDoa)}
        </span>
      );
    },
  };

  const dodColumn: ColumnsType<ReconBillUploadRow>[number] = {
    title: "DOD",
    dataIndex: "aiDod",
    key: "aiDod",
    onHeaderCell: headerCellStyle,
    onCell: (record) => ({
      style: { background: record.cancelled ? "#f0f0f0" : "#f5f5f5" },
    }),
    render: (_value, record) => {
      if (shouldShowOcrSpinner(record, record.aiDod)) {
        return <Spin size="small" />;
      }
      if (record.cancelled) {
        return (
          <span style={{ color: CANCELLED_TEXT }}>
            {formatDateOnly(record.aiDod)}
          </span>
        );
      }
      const mismatch =
        !isSyntheticReconRow(record) &&
        isDateMismatch(record.aiDod, record.dod);
      return (
        <span
          style={{
            color: mismatch ? MISMATCH_RED : DATA_BLUE,
            fontWeight: 600,
          }}
        >
          {formatDateOnly(record.aiDod)}
        </span>
      );
    },
  };

  const amountColumn: ColumnsType<ReconBillUploadRow>[number] = {
    title: "AMOUNT",
    dataIndex: "cghsTotal",
    key: "cghsTotal",
    onHeaderCell: headerCellStyle,
    onCell: (record) => {
      const mismatch =
        !record.cancelled &&
        !isSyntheticReconRow(record) &&
        isAmountMismatch(record.cghsTotal, record.billAmount);
      return {
        style: {
          background: record.cancelled ? "#f0f0f0" : "#f5f5f5",
          ...(mismatch
            ? { outline: `1px solid ${MISMATCH_RED}`, outlineOffset: -1 }
            : {}),
        },
      };
    },
    render: (value, record) => {
      if (isSyntheticReconRow(record)) {
        return blueText("-", record.cancelled);
      }
      const mismatch =
        !record.cancelled && isAmountMismatch(value, record.billAmount);
      return (
        <span
          style={{
            color: record.cancelled
              ? CANCELLED_TEXT
              : mismatch
                ? MISMATCH_RED
                : DATA_BLUE,
            fontWeight: record.cancelled ? 400 : 600,
          }}
        >
          {value ? `₹${value}` : "-"}
        </span>
      );
    },
  };

  const reconBillNoColumn: ColumnsType<ReconBillUploadRow>[number] = {
    title: "RECON BILL NO.",
    dataIndex: "billNo",
    key: "reconBillNo",
    onHeaderCell: headerCellStyle,
    onCell: (record) => {
      const mismatch = !record.ocrLoading && isBillNoMismatch(record);
      return {
        style: mismatch
          ? { outline: `1px solid ${MISMATCH_RED}`, outlineOffset: -1 }
          : undefined,
      };
    },
    render: (value, record) => {
      if (isSyntheticReconRow(record)) {
        return blueText("-", record.cancelled);
      }
      const mismatch = !record.ocrLoading && isBillNoMismatch(record);
      return (
        <span
          style={{
            color: record.cancelled
              ? CANCELLED_TEXT
              : mismatch
                ? MISMATCH_RED
                : DATA_BLUE,
            fontWeight: record.cancelled ? 400 : 600,
          }}
        >
          {value || "-"}
        </span>
      );
    },
  };

  const aiBillNoColumn: ColumnsType<ReconBillUploadRow>[number] = {
    title: "BILL NO.",
    dataIndex: "aiBillNo",
    key: "aiBillNo",
    onHeaderCell: headerCellStyle,
    onCell: (record) => {
      const mismatch = !record.ocrLoading && isBillNoMismatch(record);
      return {
        style: {
          background: record.cancelled ? "#f0f0f0" : "#f5f5f5",
          ...(mismatch
            ? { outline: `1px solid ${MISMATCH_RED}`, outlineOffset: -1 }
            : {}),
        },
      };
    },
    render: (value, record) => {
      if (isSyntheticReconRow(record)) {
        return blueText("-", record.cancelled);
      }
      if (shouldShowOcrSpinner(record, value)) {
        return <Spin size="small" />;
      }
      const mismatch = isBillNoMismatch(record);
      return (
        <span
          style={{
            color: record.cancelled
              ? CANCELLED_TEXT
              : mismatch
                ? MISMATCH_RED
                : DATA_BLUE,
            fontWeight: record.cancelled ? 400 : 600,
          }}
        >
          {value || "-"}
        </span>
      );
    },
  };

  const cancelledColumns: ColumnsType<ReconBillUploadRow> = [
    {
      title: "CANCELLED BILL",
      key: "cancelledBill",
      width: 130,
      align: "center",
      onHeaderCell: headerCellStyle,
      render: (_value, record) => {
        if (isSyntheticReconRow(record)) {
          return <span style={{ color: "#999" }}>-</span>;
        }
        if (record.cancelled) {
          return <Checkbox checked disabled />;
        }
        if (uploadDisabled || !onCancelBill) {
          return <span style={{ color: "#999" }}>-</span>;
        }
        const isRowCancelling = cancellingReconIds.includes(record.reconId);
        const isRowUploading = uploadingReconIds.includes(record.reconId);
        const isRowDeleting = deletingReconIds.includes(record.reconId);
        return (
          <Checkbox
            checked={
              pendingCancelReconId === record.reconId ||
              cancellingReconIds.includes(record.reconId)
            }
            disabled={
              isRowCancelling ||
              isRowUploading ||
              isRowDeleting ||
              pendingCancelReconId === record.reconId
            }
            onChange={(e) => {
              if (e.target.checked) {
                onCancelBill(record);
              }
            }}
          />
        );
      },
    },
    {
      title: "CANCELLED REASON",
      key: "cancelledReason",
      width: 180,
      onHeaderCell: headerCellStyle,
      render: (_value, record) =>
        record.cancelled && !isSyntheticReconRow(record) ? (
          <span style={{ color: CANCELLED_TEXT }}>
            {record.cancelledReason || "-"}
          </span>
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
    },
    {
      title: "CANCELLED BY",
      key: "cancelledByName",
      width: 140,
      onHeaderCell: headerCellStyle,
      render: (_value, record) =>
        record.cancelled && !isSyntheticReconRow(record) ? (
          <span style={{ color: CANCELLED_TEXT }}>
            {record.cancelledByName || "-"}
          </span>
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
    },
  ];

  const uploadColumn: ColumnsType<ReconBillUploadRow>[number] = {
    title: "UPLOAD",
    key: "upload",
    onHeaderCell: headerCellStyle,
    render: (_value, record) => renderUploadCell(record),
  };

  const documentKindColumn: ColumnsType<ReconBillUploadRow>[number] = {
    title: "DOCUMENT",
    key: "documentKind",
    width: 100,
    onHeaderCell: headerCellStyle,
    render: (_value, record) =>
      blueText(getDocumentKindLabel(record), record.cancelled),
  };

  const opColumns: ColumnsType<ReconBillUploadRow> = [
    documentKindColumn,
    {
      title: "SR.",
      width: 60,
      onHeaderCell: headerCellStyle,
      render: (_value, record, index) => blueText(index + 1, record.cancelled),
    },
    {
      title: "BILL DATE",
      dataIndex: "billDate",
      key: "billDate",
      onHeaderCell: headerCellStyle,
      render: (value, record) =>
        blueText(isSyntheticReconRow(record) ? "-" : value, record.cancelled),
    },
    reconBillNoColumn,
    {
      title: "BILL AMOUNT",
      dataIndex: "billAmount",
      key: "billAmount",
      onHeaderCell: headerCellStyle,
      render: (value, record) =>
        blueText(
          isSyntheticReconRow(record) ? "-" : value ? `₹${value}` : "-",
          record.cancelled,
        ),
    },
    patientNameColumn,
    aiBillNoColumn,
    uhidColumn,
    amountColumn,
    ...cancelledColumns,
    uploadColumn,
  ];

  const ipColumns: ColumnsType<ReconBillUploadRow> = [
    documentKindColumn,
    {
      title: "IP NUMBER",
      dataIndex: "ipNumber",
      key: "ipNumber",
      onHeaderCell: headerCellStyle,
      render: (value, record) => blueText(value, record.cancelled),
    },
    reconBillNoColumn,
    {
      title: "BILL AMOUNT",
      dataIndex: "billAmount",
      key: "billAmount",
      onHeaderCell: headerCellStyle,
      render: (value, record) =>
        blueText(
          isSyntheticReconRow(record) ? "-" : value ? `₹${value}` : "-",
          record.cancelled,
        ),
    },
    patientNameColumn,
    aiBillNoColumn,
    doaColumn,
    dodColumn,
    uhidColumn,
    amountColumn,
    ...cancelledColumns,
    uploadColumn,
  ];

  const columns = isIpEmergency ? ipColumns : opColumns;

  return (
    <div className="recon-bill-upload-table">
      {hasMultiSelection && !uploadDisabled && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 12,
            marginBottom: 12,
            padding: "8px 12px",
            background: "#f5f8fc",
            border: "1px solid #d9e6f2",
            borderRadius: 6,
          }}
        >
          <span style={{ color: DATA_BLUE, fontSize: 13 }}>
            Upload for {selectedRowsWithoutDocument.length} selected bill(s)
          </span>
          <Upload
            accept=".pdf"
            showUploadList={false}
            disabled={
              selectedRowsWithoutDocument.length === 0 || isBulkUploading
            }
            beforeUpload={(file) => {
              if (selectedRowsWithoutDocument.length === 0) {
                return false;
              }
              onUpload(file, [...selectedRowsWithoutDocument]);
              return false;
            }}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={isBulkUploading}
              disabled={selectedRowsWithoutDocument.length === 0}
              style={{ backgroundColor: DATA_BLUE }}
            >
              Upload Document
            </Button>
          </Upload>
        </div>
      )}
      <Table
        rowKey="reconId"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="small"
        rowClassName={(record) =>
          record.cancelled ? "recon-bill-cancelled-row" : "recon-bill-active-row"
        }
        rowSelection={
          uploadDisabled
            ? undefined
            : {
                selectedRowKeys: selectedIds,
                onChange: (keys) =>
                  onSelectionChange((keys as (string | number)[]).map(Number)),
                getCheckboxProps: (record) => ({
                  disabled:
                    !!record.cancelled ||
                    !!record.documentId ||
                    isSyntheticReconRow(record) ||
                    cancellingReconIds.includes(record.reconId) ||
                    pendingCancelReconId === record.reconId,
                }),
              }
        }
        scroll={{ x: true }}
      />
      <style>{`
        .recon-bill-upload-table .ant-table-thead > tr > th {
          background: ${HEADER_BG} !important;
          color: ${HEADER_COLOR} !important;
        }
        .recon-bill-upload-table .ant-table-thead > tr > th::before {
          display: none !important;
        }
        .recon-bill-upload-table .ant-checkbox-checked .ant-checkbox-inner {
          background-color: ${HEADER_BG} !important;
          border-color: ${HEADER_BG} !important;
        }
        .recon-bill-active-row td {
          background: #ffffff;
          color: ${DATA_BLUE};
          font-weight: 600;
        }
        .recon-bill-active-row:nth-child(odd) td {
          background: #f7f9fc;
        }
        .recon-bill-cancelled-row td {
          background: #e8e8e8 !important;
          color: ${CANCELLED_TEXT};
          font-weight: 400;
        }
      `}</style>
    </div>
  );
};

export default ReconBillUploadTable;
